import z from "zod"

import { CategoryStylingParametersMapper } from "@tfg/types/schemas/communication-studio/stylingParameters"
import type { ContentFilteringProps } from "@tfg/types/types/communication-studio/configurations.types"

import {
  CategorySchemaRules,
  CategoryKey,
  SCHEMA_RULES_METADATA,
  SchemaRule,
  COMPARISON_OPERATORS,
  RuleCondition,
  SCHEMA_OPERATIONS,
  ComparisonOperator,
} from "~/pages/Brand/CommunicationStudio/Content/rules/rules"

export class CategorySchemaRuleEngine {
  private rulesMap: Map<CategoryKey, CategorySchemaRules>

  constructor(rules: CategorySchemaRules[]) {
    this.rulesMap = new Map(rules.map((r) => [r.category, r]))
  }

  getSchema(
    category: CategoryKey,
    configuration?: {
      contentFiltering?: Partial<ContentFilteringProps>
    }
  ): z.ZodObject<any, any> {
    const categoryRules = this.rulesMap.get(category)

    if (!categoryRules) {
      return CategoryStylingParametersMapper[category]
    }

    const sortedRules = [...categoryRules.rules].sort(
      (a, b) => (b.priority ?? 0) - (a.priority ?? 0)
    )

    for (const rule of sortedRules) {
      if (this.evaluateConditions(rule.conditions, configuration)) {
        return this.applyRule(category, rule)
      }
    }

    if (categoryRules.defaultRule) {
      return this.applyRule(category, categoryRules.defaultRule)
    }

    return CategoryStylingParametersMapper[category]
  }

  private evaluateConditions(
    conditions: RuleCondition[],
    configuration?: {
      contentFiltering?: Partial<ContentFilteringProps>
    }
  ): boolean {
    return conditions.every((condition) =>
      this.evaluateCondition(condition, configuration)
    )
  }

  private evaluateCondition(
    condition: RuleCondition,
    configuration?: {
      contentFiltering?: Partial<ContentFilteringProps>
    }
  ): boolean {
    const value = this.getConfigValue(condition.configPath, configuration)

    const comparisonOperatorResolver: Record<
      ComparisonOperator,
      (conditionValue: unknown) => boolean
    > = {
      [COMPARISON_OPERATORS.EQUALS]: (conditionValue: unknown) =>
        value === conditionValue,
      [COMPARISON_OPERATORS.NOT_EQUALS]: (conditionValue: unknown) =>
        value !== conditionValue,
      [COMPARISON_OPERATORS.IN]: (conditionValue: unknown) =>
        Array.isArray(conditionValue) && conditionValue.includes(value),
      [COMPARISON_OPERATORS.NOT_IN]: (conditionValue: unknown) =>
        Array.isArray(conditionValue) && !conditionValue.includes(value),
      [COMPARISON_OPERATORS.EXISTS]: () =>
        value !== undefined && value !== null,
      [COMPARISON_OPERATORS.NOT_EXISTS]: () =>
        value === undefined || value === null,
    }

    return (
      comparisonOperatorResolver[condition.operator](condition.value) || false
    )
  }

  private getConfigValue(
    path: string,
    configuration?: {
      contentFiltering?: Partial<ContentFilteringProps>
    }
  ): unknown {
    const parts = path.split(".")
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let current: any = configuration

    for (const part of parts) {
      if (current == null) {
        return undefined
      }
      current = current[part]
    }

    return current
  }

  private getCategorySchema<
    C extends keyof typeof CategoryStylingParametersMapper,
  >(category: C): z.ZodObject<any, any> {
    return CategoryStylingParametersMapper[category]
  }

  private applyRule(
    category: CategoryKey,
    rule: Omit<SchemaRule, "conditions">
  ): z.ZodObject<any, any> {
    const baseSchema = this.getCategorySchema(category)

    switch (rule.operation) {
      case SCHEMA_OPERATIONS.PICK:
        if (!rule.fields || rule.fields.length === 0) {
          return baseSchema
        }
        return baseSchema.pick(
          Object.fromEntries(rule.fields.map((f) => [f, true]))
        )

      case SCHEMA_OPERATIONS.OMIT:
        if (!rule.fields || rule.fields.length === 0) {
          return baseSchema
        }
        return baseSchema.omit(
          Object.fromEntries(rule.fields.map((f) => [f, true]))
        )

      case SCHEMA_OPERATIONS.PICK_ALL:
        return baseSchema

      default:
        return baseSchema
    }
  }

  updateRules(categoryRules: CategorySchemaRules): void {
    this.rulesMap.set(categoryRules.category, categoryRules)
  }

  getRules(category: CategoryKey): CategorySchemaRules | undefined {
    return this.rulesMap.get(category)
  }
}

export const categorySchemaRuleEngine = new CategorySchemaRuleEngine(
  SCHEMA_RULES_METADATA
)

export function getSchemaByCategoryWithRules(
  category: CategoryKey,
  configuration?: {
    contentFiltering?: Partial<ContentFilteringProps>
  }
): z.ZodObject<any, any> {
  return categorySchemaRuleEngine.getSchema(category, configuration)
}
